import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Modal } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import ChooseAFolderInDr from 'router/subrouters/DataRoom/components/ChooseAFolderInDr';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import classNames from 'classnames';
import { FileTypeRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { getIsRfiFileStatusUploaded } from 'router/subrouters/DataRoom/utils';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { DocumentPublishedEventProps } from 'models/MixpanelEvents';
import mixpanelService from 'services/mixpanelService';

import './AddDocToDataRoomModal.styles.scss';

type AddDocToDataRoomModalProps = {
  className?: string;
  dataRoomRfiCategoryId: string;
  document: FileTypeRFI;
  onAddSuccess: (file?: FileTypeRFI) => void;
};

const AddDocToDataRoomModal: ForwardRefRenderFunction<
  ModalRef,
  AddDocToDataRoomModalProps
> = (props, ref) => {
  const { className, document, dataRoomRfiCategoryId, onAddSuccess } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [nodeToMoveTo, setNodeToMoveTo] = useState<string>(null);

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id: dataRoomId, name },
    isOwner,
    qaSide,
  } = useContext(DataRoomContext);

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const isNewFile = getIsRfiFileStatusUploaded(document?.status);

  const currentLocation = useMemo(
    () =>
      !document?.dataRoomNodeId
        ? null
        : {
            parentId: document?.parentId || null,
            parentName: document?.parentName,
          },
    [document],
  );

  const onSubmit = useCallback(async () => {
    try {
      setIsLoading(true);

      if (isNewFile)
        mixpanelService.documentPublished(
          getMixpanelProps({
            dataRoomId,
            dataRoomName: name,
            documentName: document.file?.name,
            documentType: document.file?.type,
            isOwner,
            userSide: qaSide,
          }) as DocumentPublishedEventProps,
        );

      const { data } = await dataRoomApi.rfi[
        isNewFile ? 'addDocumentToDR' : 'moveDocumentWithinDR'
      ](dataRoomRfiCategoryId, document._id, nodeToMoveTo);

      if (data) onAddSuccess(data);

      modal.close();
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    document,
    dataRoomRfiCategoryId,
    isNewFile,
    modal,
    nodeToMoveTo,
    name,
    dataRoomId,
    isOwner,
    qaSide,
    getMixpanelProps,
    onAddSuccess,
    popServerError,
  ]);

  const classes = classNames('add-doc-to-data-room-modal', className);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={
        <span className="add-doc-to-data-room-modal__title">
          {t(
            isNewFile ? 'addDocumentToDataRoom' : 'moveDocumentWithinDataRoom',
            { documentName: document?.file?.name },
          )}
        </span>
      }
    >
      {isLoading && <Loader overlay={false} />}

      <ChooseAFolderInDr
        dataRoomId={dataRoomId}
        currenntlySelectedNodeId={nodeToMoveTo}
        currentLocation={currentLocation}
        onChange={setNodeToMoveTo}
      />

      <div className="add-doc-to-data-room-modal__actions">
        <Button variant="link" onClick={modal?.close}>
          {t('cancel')}
        </Button>
        <Button type="submit" onClick={onSubmit} disabled={isLoading}>
          {t('confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(AddDocToDataRoomModal);
